import { NgModule, NgZone } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { SharedModule } from './modules/shared/shared.module';
import { HttpClientModule } from '@angular/common/http';

import { environment } from '../environments/environment';

import { LoginComponent } from './pages/login/login.component';
import { EstimatesComponent } from './pages/estimates/estimates.component';
import { InvoicesComponent } from './pages/invoices/invoices.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { SearchComponent } from './components/search/search.component';
import { SignupComponent } from './pages/signup/signup.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { ProfileLogoComponent } from './components/profile-logo/profile-logo.component';
import { ProfileLogoModal } from './modals/profile-logo/profile-logo.component';
import { ProvidersRowComponent } from './components/providers-row/providers-row.component';
import { ChangeEmailComponent } from './modals/change-email/change-email.component';
import { ForgotPasswordModal } from './modals/forgot-password/forgot-password.component';
import { ChangePasswordComponent } from './modals/change-password/change-password.component';
import { ReauthenticationDialog } from './modals/reauthentication-dialog/reauthentication-dialog.component';
import { RequestInvitationModal } from './modals/request-invitation/request-invitation.component';

import { DragNDropDirective } from './directives/drag-n-drop.directive';
import { HideTooltipOnScrollDirective } from './directives/hide-tooltip-on-scroll.directive';

import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask'
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, RippleGlobalOptions, MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { LabelComponent } from './components/sent-label/label.component';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { MobileToolbarComponent } from './components/mobile-toolbar/mobile-toolbar.component';
import { ProfileFieldComponent } from './components/profile-field/profile-field.component';
import { LobbyComponent } from './pages/lobby/lobby.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { UsersComponent } from './pages/profile/users/users.component';
import { UserTileComponent } from './pages/profile/users/user-tile/user-tile.component';
import { AddUserComponent } from './modals/add-user/add-user.component';
import { ItemsComponent } from "./pages/items/items.component";
import { CreateOrEditItemComponent } from './modals/create-or-edit-item/create-or-edit-item.component';
import { JoinComponent } from './pages/join/join.component';
import { UserAddedComponent } from './modals/user-added/user-added.component';
import { ViewAsButtonComponent } from './components/view-as-button/view-as-button.component';
import { UserButtonComponent } from './components/view-as-button/user-button/user-button.component';
import { AvailabilityModalComponent } from './modals/availability-modal/availability-modal.component';
import { UserSelectComponent } from './components/user-select/user-select.component';
import { AvailabilityTileComponent } from './components/availability-tile/availability-tile.component';
import { FormsModule } from "@angular/forms";
import { OverlookComponent } from './pages/overlook/overlook.component';
import { ScheduleComponent } from './pages/schedule/schedule.component';
import { ItemUploadComponent } from './modals/item-upload/item-upload.component';
import { ItemExampleComponent } from './modals/item-example/item-example.component';
import { LeadExampleComponent } from './modals/lead-example/lead-example.component';
import { EstimateComponent } from './pages/estimate/estimate.component';
import { MenuButtonComponent } from './components/menu-button/menu-button.component';
import { EditClientComponent } from './modals/edit-client/edit-client.component';
import { SummaryPreviewModal } from './modals/summary-preview/summary-preview.component';
import { EstimateItemsListComponent } from './components/estimate-items-list/estimate-items-list.component';
import { CdkDrag, CdkDragHandle, CdkDropList } from "@angular/cdk/drag-drop";
import { EstimateSummaryComponent } from './components/estimate-summary/estimate-summary.component';
import { TimetableDialogComponent } from './modals/timetable-dialog/timetable-dialog.component';
import { TimeInputComponent } from './components/time-input/time-input.component';
import { CreateBusinessComponent } from './pages/create-business/create-business.component';
import { WorkflowItemInfoComponent } from './components/workflow-item-info/workflow-item-info.component';
import { ScrollEventDirective } from './directives/scroll-event.directive';
import { CreateEditWorkflowItemComponent } from './modals/create-edit-workflow-item/create-edit-workflow-item.component';
import { CalendarModule, DateAdapter as CalendarDateAdapter} from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { CalendarViewComponent } from './components/calendar-view/calendar-view.component';
import { WorkflowPreparedComponent } from './components/workflow-prepared/workflow-prepared.component';
import { EstimateEditableComponent } from './components/estimate-editable/estimate-editable.component';
import { EstimateActionsComponent } from './components/estimate-actions/estimate-actions.component';
import { EstimatePreviewAndSendComponent } from './components/estimate-preview-and-send/estimate-preview-and-send.component';
import { ReportsComponent } from './pages/reports/reports.component';
import { ReportsPaymentsComponent } from './pages/reports-payments/reports-payments.component';
import { ReportsCommissionsComponent } from './pages/reports-commissions/reports-commissions.component';
import { ReportsTechPartsComponent } from './pages/reports-tech-parts/reports-tech-parts.component';
import { ReportsOverlookComponent } from './pages/reports-overlook/reports-overlook.component';
import { ReportsCompareComponent } from './pages/reports-compare/reports-compare.component';
import { SalesSummaryComponent } from './components/sales-summary/sales-summary.component';
import { WorkflowItemInfoMobileComponent } from './components/workflow-item-info-mobile/workflow-item-info-mobile.component';
import { InvoiceComponent } from './pages/invoice/invoice.component';
import { InvoiceActionsComponent } from './components/invoice-actions/invoice-actions.component';
import { InvoiceEditableComponent } from './components/invoice-editable/invoice-editable.component';
import { InvoiceItemsListComponent } from './components/invoice-items-list/invoice-items-list.component';
import { InvoiceSummaryComponent } from './components/invoice-summary/invoice-summary.component';
import { InvoicePreviewAndSendComponent } from './components/invoice-preview-and-send/invoice-preview-and-send.component';
import { PersonalComponent } from './pages/profile/personal/personal.component';
import { BusinessComponent } from './pages/profile/business/business.component';
import { RoleLabelComponent } from './pages/profile/users/role-label/role-label.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { PaymentComponent } from './pages/payment/payment.component';
import { PaymentSummaryComponent } from './components/payment-summary/payment-summary.component';
import { PaymentActionsComponent } from './components/payment-actions/payment-actions.component';
import { PaymentItemComponent } from './components/payment-item/payment-item.component';
import { AddEditPaymentComponent } from './modals/add-edit-payment/add-edit-payment.component';
import { RefundPaymentComponent } from './modals/refund-payment/refund-payment.component';
import { EditWorkerPaymentComponent } from './modals/edit-worker-payment/edit-worker-payment.component';
import { CustomerListComponent } from "./pages/customer-list/customer-list.component";
import { CustomerItemsListComponent } from './components/customer-items-list/customer-items-list.component';
import { CustomerTileComponent } from './components/customer-tile/customer-tile.component';
import { SelectBusinessButtonComponent } from './components/select-business-button/select-business-button.component';
import { BusinessButtonComponent } from './components/select-business-button/business-button/business-button.component';
import { UserSearchBarComponent } from './components/user-search-bar/user-search-bar.component';
import { AssignedComponent } from './pages/assigned/assigned.component';
import { AssignmentComponent } from './pages/assignment/assignment.component';
import { DatePipe } from "@angular/common";
import { AssignedDialogComponent } from './modals/assigned-dialog/assigned-dialog.component';
import { AssignmentDialogComponent } from './modals/assignment-dialog/assignment-dialog.component';
import { AssignTileComponent } from './components/assign-tile/assign-tile.component';
import { CreateEditWorkflowItemRestrictedComponent } from './modals/create-edit-workflow-item-restricted/create-edit-workflow-item-restricted.component';
import { SuspendedBusinessComponent } from './pages/suspended-business/suspended-business.component';
import { ResetComponent } from './pages/reset/reset.component';
import { GoogleAuthResultComponent } from './pages/google-auth-result/google-auth-result.component';
import { GoogleAuthComponent } from './pages/google-auth/google-auth.component';
import { DynamicListComponent } from './components/dynamic-list/dynamic-list.component';
import { ItemTileComponent } from './components/item/item-tile.component';
import { LeadsComponent } from './pages/leads/leads.component';
import { LeadImportComponent } from './pages/lead-import/lead-import.component';
import { LeadTileComponent } from './components/lead-tile/lead-tile.component';
import { BaseTileComponent } from './components/base-tile/base-tile.component';
import { AssessmentsComponent } from './pages/assessments/assessments.component';
import { AssessmentTileComponent } from './components/assessment-tile/assessment-tile.component';
import { JobsComponent } from './pages/jobs/jobs.component';
import { JobTileComponent } from './components/job-tile/job-tile.component';
import { LoadingComponent } from './components/loading/loading.component';
import { AvailabilityComponent } from './pages/availability/availability.component';
import { VersionPickerComponent } from './components/version-picker/version-picker.component';
import { BaseTileTimeComponent } from './components/base-tile/base-tile-time/base-tile-time.component';
import { TechPartsReportItemsComponent } from './components/tech-parts-report-items/tech-parts-report-items.component';
import { TechPartsReportTileComponent } from './components/tech-parts-report-tile/tech-parts-report-tile.component';
import { CommissionsReportItemsComponent } from './components/commissions-report-items/commissions-report-items.component';
import { CommissionsReportTileComponent } from './components/commissions-report-tile/commissions-report-tile.component';
import { PaymentsReportItemsComponent } from './components/payments-report-items/payments-report-items.component';
import { PaymentsReportTileComponent } from './components/payments-report-tile/payments-report-tile.component';
import { AllScheduleComponent } from './pages/all-schedule/all-schedule.component';
import { RatingComponent } from "../../../common/src/lib/components/rating/rating.component";
import { HelpComponent } from "./pages/profile/help/help.component";
import { RequestInvitationFormComponent } from "./components/request-invitation-form/request-invitation-form.component";
import { EoIListComponent } from './pages/eoi-list/eoi-list.component';
import { EoITileComponent } from './components/eoi-tile/eoi-tile.component';
import { HideTextDirective } from "./directives/hide-text.directive";
import { ChatComponent } from './components/chat/chat.component';
import { NotificationsButtonComponent } from './components/notifications-button/notifications-button.component';
import { ChatsListComponent } from './components/chats-list/chats-list.component';
import { NotificationsListComponent } from './components/notifications-list/notifications-list.component';
import { BusinessChatContentComponent } from './components/business-chat-content/business-chat-content.component';
import { SupabaseService } from './services/supabase.service';
import { ChatMessageInputComponent } from "./components/chat-message-input/chat-message-input.component";
import { OnboardingBannerComponent } from './components/onboarding-banner/onboarding-banner.component';
import { AfterLoginComponent } from './components/after-login/after-login.component';
import { WsErrorBannerComponent } from './components/ws-error-banner/ws-error-banner.component';
import { ShortNumberDirective } from './directives/short-number.directive';
import { NuveiApplicationComponent } from './components/nuvei-application/nuvei-application.component';
import { NuveiApplicationDocumentsComponent } from './components/nuvei-application/nuvei-application-documents/nuvei-application-documents.component';
import { NuveiApplicationLegalComponent } from './components/nuvei-application/nuvei-application-legal/nuvei-application-legal.component';
import { NuveiApplicationDbaComponent } from './components/nuvei-application/nuvei-application-dba/nuvei-application-dba.component';
import { NuveiApplicationAdditionalComponent } from './components/nuvei-application/nuvei-application-additional/nuvei-application-additional.component';
import { NuveiApplicationSalesProfileComponent } from './components/nuvei-application/nuvei-application-sales-profile/nuvei-application-sales-profile.component';
import { NuveiApplicationOwnersComponent } from './components/nuvei-application/nuvei-application-owners/nuvei-application-owners.component';
import { NuveiApplicationBankComponent } from './components/nuvei-application/nuvei-application-bank/nuvei-application-bank.component';
import { NuveiApplicationSummaryComponent } from './components/nuvei-application/nuvei-application-summary/nuvei-application-summary.component';
import { SignatureComponent } from "./modals/signature/signature.component";
import { FileTestComponent } from './components/file-test/file-test.component';
import { AddressFormFieldComponent } from './components/address-form-field/address-form-field.component';
import { NuveiApplicationPreviewComponent } from './components/nuvei-application/nuvei-application-preview/nuvei-application-preview.component';
import { NuveiApplicationGuarantorComponent } from './components/nuvei-application/nuvei-application-guarantor/nuvei-application-guarantor.component';
import { BannerComponent } from "./components/onboarding-banner/banner.component";
import { AssessmentComponent } from './pages/event-info/assessment-or-job/assessment.component';
import { JobComponent } from './pages/event-info/assessment-or-job/job.component';
import { ReportsTipsComponent } from './pages/reports-tips/reports-tips.component';
import { TipsReportItemsComponent } from './components/tips-report-items/tips-report-items.component';
import { TipsReportTileComponent } from './components/tips-report-tile/tips-report-tile.component';
import { NotificationTileComponent } from './components/notification-tile/notification-tile.component';
import { MissingNuveiComponent } from './components/missing-nuvei/missing-nuvei.component';
import { ReportsCreditCardComponent } from './pages/reports-credit-card/reports-credit-card.component';
import { CreditCardReportItemsComponent } from './components/credit-card-report-items/credit-card-report-items.component';
import { CreditCardReportTileComponent } from './components/credit-card-report-tile/credit-card-report-tile.component';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MMM DD, YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
    monthLabel: 'MMMM',
  },
};

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0
  }
};

export const zone = new NgZone({});
const supabaseService = new SupabaseService(environment.supabase, zone);

@NgModule({
  declarations: [
    AppComponent,
    ToolbarComponent,
    LoginComponent,
    EstimatesComponent,
    InvoicesComponent,
    SearchComponent,
    SignupComponent,
    ForgotPasswordComponent,
    ProfileComponent,
    ProfileLogoComponent,
    ProfileLogoModal,
    ProvidersRowComponent,
    DragNDropDirective,
    HideTooltipOnScrollDirective,
    ChangeEmailComponent,
    ForgotPasswordModal,
    ChangePasswordComponent,
    ReauthenticationDialog,
    RequestInvitationModal,
    LabelComponent,
    EmptyStateComponent,
    MobileToolbarComponent,
    ProfileFieldComponent,
    LobbyComponent,
    UsersComponent,
    UserTileComponent,
    AddUserComponent,
    ItemsComponent,
    CreateOrEditItemComponent,
    JoinComponent,
    UserAddedComponent,
    ViewAsButtonComponent,
    UserButtonComponent,
    EstimateComponent,
    AvailabilityModalComponent,
    UserSelectComponent,
    AvailabilityTileComponent,
    OverlookComponent,
    ScheduleComponent,
    ItemUploadComponent,
    ItemExampleComponent,
    LeadExampleComponent,
    MenuButtonComponent,
    SummaryPreviewModal,
    TimetableDialogComponent,
    TimeInputComponent,
    CreateBusinessComponent,
    EditClientComponent,
    EstimateItemsListComponent,
    EstimateSummaryComponent,
    WorkflowItemInfoComponent,
    ScrollEventDirective,
    HideTextDirective,
    CreateEditWorkflowItemComponent,
    WorkflowPreparedComponent,
    EstimateEditableComponent,
    EstimateActionsComponent,
    EstimatePreviewAndSendComponent,
    WorkflowItemInfoMobileComponent,
    CalendarViewComponent,
    InvoiceComponent,
    InvoiceActionsComponent,
    InvoiceEditableComponent,
    InvoiceItemsListComponent,
    InvoiceSummaryComponent,
    InvoicePreviewAndSendComponent,
    PersonalComponent,
    BusinessComponent,
    RoleLabelComponent,
    ProgressBarComponent,
    PaymentComponent,
    PaymentSummaryComponent,
    PaymentActionsComponent,
    PaymentItemComponent,
    CalendarViewComponent,
    ReportsComponent,
    ReportsPaymentsComponent,
    ReportsCommissionsComponent,
    ReportsTechPartsComponent,
    ReportsOverlookComponent,
    ReportsCompareComponent,
    SalesSummaryComponent,
    AddEditPaymentComponent,
    RefundPaymentComponent,
    CustomerListComponent,
    CustomerItemsListComponent,
    CustomerTileComponent,
    EditWorkerPaymentComponent,
    SelectBusinessButtonComponent,
    BusinessButtonComponent,
    UserSearchBarComponent,
    AssignedComponent,
    AssignmentComponent,
    AssignedDialogComponent,
    CreateEditWorkflowItemRestrictedComponent,
    AssignTileComponent,
    AssignmentDialogComponent,
    SuspendedBusinessComponent,
    ResetComponent,
    GoogleAuthResultComponent,
    GoogleAuthComponent,
    DynamicListComponent,
    ItemTileComponent,
    LeadsComponent,
    LeadTileComponent,
    BaseTileComponent,
    AssessmentsComponent,
    AssessmentTileComponent,
    JobsComponent,
    JobTileComponent,
    LoadingComponent,
    AvailabilityComponent,
    VersionPickerComponent,
    BaseTileTimeComponent,
    TechPartsReportItemsComponent,
    TechPartsReportTileComponent,
    CommissionsReportItemsComponent,
    CommissionsReportTileComponent,
    PaymentsReportItemsComponent,
    PaymentsReportTileComponent,
    AllScheduleComponent,
    RatingComponent,
    HelpComponent,
    RequestInvitationFormComponent,
    EoIListComponent,
    EoITileComponent,
    ChatComponent,
    NotificationsButtonComponent,
    ChatsListComponent,
    NotificationsListComponent,
    BusinessChatContentComponent,
    ChatMessageInputComponent,
    OnboardingBannerComponent,
    AfterLoginComponent,
    WsErrorBannerComponent,
    ShortNumberDirective,
    NuveiApplicationComponent,
    NuveiApplicationDocumentsComponent,
    NuveiApplicationLegalComponent,
    NuveiApplicationDbaComponent,
    NuveiApplicationAdditionalComponent,
    NuveiApplicationSalesProfileComponent,
    NuveiApplicationOwnersComponent,
    NuveiApplicationBankComponent,
    NuveiApplicationSummaryComponent,
    SignatureComponent,
    FileTestComponent,
    AddressFormFieldComponent,
    NuveiApplicationPreviewComponent,
    NuveiApplicationGuarantorComponent,
    AssessmentComponent,
    JobComponent,
    ReportsTipsComponent,
    TipsReportItemsComponent,
    TipsReportTileComponent,
    NotificationTileComponent,
    MissingNuveiComponent,
    NotificationTileComponent,
    TipsReportTileComponent,
    ReportsCreditCardComponent,
    CreditCardReportItemsComponent,
    CreditCardReportTileComponent,
    LeadImportComponent
  ],
  imports: [
      BrowserModule,
      AppRoutingModule,
      BrowserAnimationsModule,
      SharedModule,
      HttpClientModule,
      ClipboardModule,
      MatNativeDateModule,
      MatDatepickerModule,
      ServiceWorkerModule.register('service-worker.js', {
          enabled: environment.production,
          // Register the ServiceWorker as soon as the application is stable
          // or after 30 seconds (whichever comes first).
          registrationStrategy: 'registerWhenStable:60000'
      }),
      FormsModule,
      CdkDrag,
      CdkDragHandle,
      CdkDropList,
      CalendarModule.forRoot({
          provide: CalendarDateAdapter,
          useFactory: adapterFactory
      }),
      NgxMaskDirective,
      NgxMaskPipe,
      BannerComponent
  ],
  providers: [
    provideEnvironmentNgxMask(),
    MatDatepickerModule,
    DatePipe,
    { provide: Window, useValue: window },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: 'DOMAIN', useValue: environment.domain },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: 'SupabaseService', useValue: supabaseService },
    { provide: SupabaseService, useValue: supabaseService },
    { provide: NgZone, useValue: zone },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }